import { useCallback, useEffect, useState } from 'react';
import {
  attachMenaosRegistration,
  createAllCorpusInvitations,
  createCorpusInvitation,
  dismissMenaosRegistration,
  getMenaosSync,
  listAllCorpusInvitations,
  listCorpusInvitations,
  listMenaosUnmatched,
  markCorpusInvitationSent,
  restartMenaosSync,
  resumeCompanyInvitations,
  reviewMatchedRegistration,
  revokeCorpusInvitation,
  runMenaosSync,
  sendInvitationEmail,
  stopCompanyInvitations,
} from '../api/client';
import EmailTemplateModal from './EmailTemplateModal';

export const INVITATION_LABELS = {
  not_invited: 'Not invited',
  link_ready: 'Link ready',
  sent: 'Sent',
  registered: 'Registered',
  review_needed: 'Review needed',
  review_rejected: 'Not a match — decide',
  registered_revoked: 'Registered (revoked link)',
  revoked: 'Revoked',
};

// A reviewer's decision on a matched registration (GTM-41).
export const REVIEW_LABELS = {
  confirmed_contact: 'Confirmed contact',
  company_only: 'Company confirmed, not the person',
  not_a_match: 'Not a match',
  attached: 'Attached by an admin',
};

// What a registration proves, in the same words the MenaOS CRM uses.
export const NOT_CONFIRMED =
  "This link was used. The person's identity and company have not been confirmed. Follow-up happens in MenaOS.";

// Faisal's wording (2026-11-04), the same text as the emailed invitation (backend invitation_email).
export function invitationMessage(person, company, link) {
  const first = (person.full_name || '').split(' ')[0] || 'there';
  return [
    `Hi ${first},`,
    '',
    'We are inviting a select group of companies to join Project Corpus. We partnered with AI labs to acquire anonymized company data with payouts ranging from $100k to $2m. For those interested, apply by November 12th. It only takes 2-3 minutes.',
    '',
    'About us: MENADevs is an AI deployment lab with 150+ AI engineers. We build AI systems for enterprises and governments across 25+ global clients. We are a Certified Anthropic Partner and OpenAI Select Partner.',
    '',
    'The project: AI labs need real company data to train AI that can do real work, like the history in your Drive, CRM and Slack. We (or you) anonymize it, license it to the labs, and you get paid. No customer or employee data leaves your control.',
    '',
    '- Payouts range from $100k - $2m',
    '- Minimum 30 employees at peak',
    '- Full corpus: CRM, ERP, Slack, Drive, QB, etc.',
    '- Minimum 3 years operational',
    '',
    'We have set up a short form to give you an estimate if you are interested:',
    link,
  ].join('\n');
}

function formatWhen(value) {
  if (!value) return '';
  return new Date(value).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function ago(value) {
  if (!value) return '';
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return formatWhen(value);
}

const NO_RESPONSE_DAYS = 14;

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function CopyButton({ text, label, onCopied }) {
  const [state, setState] = useState('');
  async function copy() {
    const ok = await copyText(text);
    setState(ok ? 'Copied' : 'Select and copy');
    if (ok && onCopied) onCopied();
    setTimeout(() => setState(''), 2000);
  }
  return <button type="button" onClick={copy}>{state || label}</button>;
}

function Timeline({ invitation }) {
  return (
    <ol className="corpus-invite-timeline">
      <li><strong>Link created</strong> · {formatWhen(invitation.created_at)} by {invitation.created_by}</li>
      {invitation.sent_at && <li><strong>Marked as sent</strong> · {formatWhen(invitation.sent_at)}</li>}
      {invitation.revoked_at && (
        <li><strong>Revoked</strong> · {formatWhen(invitation.revoked_at)} · {invitation.revoked_reason}</li>
      )}
      {invitation.registrations.map((r) => (
        <li key={r.reference}>
          <strong>Registered through this link</strong> · {formatWhen(r.submitted_at)} · MenaOS <span className="corpus-mono">{r.reference}</span>
          {r.review_state && r.review_state !== 'review_needed' && (
            <> · {REVIEW_LABELS[r.review_state] || r.review_state}{r.reviewed_by ? ` by ${r.reviewed_by}` : ''}</>
          )}
        </li>
      ))}
    </ol>
  );
}

// A used link can be forwarded, so a reviewer confirms who registered (GTM-41).
function RegistrationReview({ registration, onChanged }) {
  const [decision, setDecision] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await reviewMatchedRegistration(registration.reference, decision, note.trim());
      await onChanged();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  const name = `decision-${registration.reference}`;
  return (
    <form className="corpus-review" onSubmit={submit}>
      <p className="corpus-hint">
        A used link can be forwarded, so confirm who registered (MenaOS <span className="corpus-mono">{registration.reference}</span>):
      </p>
      <div className="corpus-review-choices">
        {[
          ['confirmed_contact', 'Confirmed contact'],
          ['company_only', 'Company confirmed, not the person'],
          ['not_a_match', 'Not a match'],
        ].map(([value, label]) => (
          <label key={value}>
            <input type="radio" name={name} value={value} checked={decision === value} onChange={(e) => setDecision(e.target.value)} /> {label}
          </label>
        ))}
      </div>
      <input
        aria-label={`Note for ${registration.reference}`}
        placeholder="Why, e.g. spoke to her directly"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {error && <div className="corpus-error" role="alert">{error}</div>}
      <button type="submit" disabled={busy || !decision || note.trim().length < 3}>{busy ? 'Saving…' : 'Save decision'}</button>
    </form>
  );
}

function PersonInvitation({ company, person, invitation, canCreate, linksConfigured, blocked, canEmail, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [composing, setComposing] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const state = invitation ? invitation.state : 'not_invited';
  const live = invitation && !invitation.revoked_at;

  async function act(fn) {
    setBusy(true);
    setError('');
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const stale = state === 'sent' && invitation.sent_at
    && (Date.now() - new Date(invitation.sent_at).getTime()) / 86400000 >= NO_RESPONSE_DAYS;

  return (
    <li className={`corpus-invite state-${state}`}>
      <div className="corpus-invite-head">
        <div>
          <strong>{person.full_name}</strong>
          {person.priority === 'first' && <span className="corpus-badge priority-first">First contact</span>}
          {person.priority === 'backup' && <span className="corpus-badge priority-backup">Backup</span>}
          <div className="corpus-line">{person.title}</div>
        </div>
        <span className="corpus-invite-badges">
          {blocked && <span className="corpus-badge invite-blocked">Do not contact</span>}
          <span className={`corpus-badge invite-${state}`}>{INVITATION_LABELS[state]}</span>
        </span>
      </div>
      {blocked && (
        <p className="corpus-hint">
          This person is on the Do not contact list, so no link can be made or sent.{live ? ' Revoke this link if it should stop working.' : ''}
        </p>
      )}

      {error && <div className="corpus-error" role="alert">{error}</div>}

      {!live && canCreate && !blocked && (
        <div className="corpus-actions">
          <button
            type="button"
            className="approve"
            disabled={busy || !linksConfigured}
            onClick={() => act(() => createCorpusInvitation(company.id, person.id))}
          >
            {busy ? 'Creating…' : invitation ? 'Create a new link' : 'Create invitation link'}
          </button>
        </div>
      )}

      {live && (
        <>
          <div className="corpus-invite-link">
            <input aria-label={`Invitation link for ${person.full_name}`} readOnly value={invitation.link || ''} onFocus={(e) => e.target.select()} />
            <CopyButton text={invitation.link || ''} label="Copy link" />
          </div>
          <div className="corpus-actions">
            {!invitation.sent_at && !blocked && canEmail && (
              <button type="button" className="approve" disabled={busy} onClick={() => setComposing(true)}>
                {busy ? 'Sending…' : 'Send email…'}
              </button>
            )}
            {!blocked && <CopyButton text={invitationMessage(person, company, invitation.link || '')} label="Copy message" />}
            {!invitation.sent_at && !blocked && (
              <button type="button" disabled={busy} onClick={() => act(() => markCorpusInvitationSent(invitation.id))}>Mark as sent</button>
            )}
            {!revoking && (!invitation.registrations.length || state === 'review_rejected') && (
              <button type="button" className="dismiss" disabled={busy} onClick={() => { setRevoking(true); setReason(''); }}>Revoke</button>
            )}
          </div>
          {composing && !invitation.sent_at && (
            <EmailTemplateModal
              title={`Email to ${person.full_name}`}
              summary="Check the email, edit it if needed, then send. Only this person is emailed."
              confirmLabel="Confirm and send"
              invitationId={invitation.id}
              onConfirm={() => act(async () => { await sendInvitationEmail(invitation.id); setComposing(false); })}
              onClose={() => setComposing(false)}
            />
          )}
          {revoking && (
            <form
              className="corpus-actions"
              onSubmit={(e) => {
                e.preventDefault();
                act(async () => { await revokeCorpusInvitation(invitation.id, reason.trim()); setRevoking(false); });
              }}
            >
              <input aria-label="Reason for revoking" placeholder="Why, e.g. sent to the wrong person" value={reason} onChange={(e) => setReason(e.target.value)} />
              <button type="submit" className="dismiss" disabled={busy || reason.trim().length < 3}>Confirm revoke</button>
              <button type="button" onClick={() => setRevoking(false)}>Cancel</button>
            </form>
          )}
          {!invitation.sent_at && !blocked && <p className="corpus-hint">Send it yourself from your email or LinkedIn. MenaGTM never sends it.</p>}
          {stale && <p className="corpus-hint">No response yet after {NO_RESPONSE_DAYS} days.</p>}
        </>
      )}

      {invitation && state === 'review_needed' && <p className="corpus-invite-note">{NOT_CONFIRMED}</p>}
      {invitation
        && invitation.registrations
          .filter((r) => r.review_state === 'review_needed')
          .map((r) => <RegistrationReview key={r.reference} registration={r} onChanged={onChanged} />)}
      {invitation && state === 'review_rejected' && (
        <p className="corpus-hint">Marked not a match. Decide manually: revoke this link, or create a new one.</p>
      )}
      {invitation && <Timeline invitation={invitation} />}
    </li>
  );
}

// Stop (or resume) inviting anyone at a whole company (GTM-41). Separate from Do not contact.
function CompanyStop({ company, stopped, onChanged }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function act(fn) {
    setBusy(true);
    setError('');
    try {
      await fn();
      await onChanged();
      setOpen(false);
      setReason('');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (stopped) {
    return (
      <div className="corpus-notice" role="status">
        Invitations to this company are stopped{stopped.reason ? `: ${stopped.reason}` : ''}. No new links can be made or sent.
        {error && <div className="corpus-error" role="alert">{error}</div>}
        {!open ? (
          <button type="button" onClick={() => setOpen(true)}>Resume invitations</button>
        ) : (
          <form className="corpus-actions" onSubmit={(e) => { e.preventDefault(); act(() => resumeCompanyInvitations(company.id, reason.trim())); }}>
            <input aria-label="Reason to resume" placeholder="Why, e.g. the deal fell through" value={reason} onChange={(e) => setReason(e.target.value)} />
            <button type="submit" disabled={busy || reason.trim().length < 3}>Confirm resume</button>
            <button type="button" onClick={() => setOpen(false)}>Cancel</button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="corpus-stop">
      {error && <div className="corpus-error" role="alert">{error}</div>}
      {!open ? (
        <button type="button" className="dismiss" onClick={() => setOpen(true)}>Stop invitations to this company</button>
      ) : (
        <form className="corpus-actions" onSubmit={(e) => { e.preventDefault(); act(() => stopCompanyInvitations(company.id, reason.trim())); }}>
          <input aria-label="Reason to stop" placeholder="Why, e.g. already a customer" value={reason} onChange={(e) => setReason(e.target.value)} />
          <button type="submit" className="dismiss" disabled={busy || reason.trim().length < 3}>Confirm stop</button>
          <button type="button" onClick={() => setOpen(false)}>Cancel</button>
        </form>
      )}
    </div>
  );
}

export function InvitationTab({ company, people, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNote, setBulkNote] = useState('');

  const approved = company.review_decision === 'approved';
  const load = useCallback(async () => {
    try {
      setData(await listCorpusInvitations(company.id));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [company.id]);

  useEffect(() => {
    setData(null);
    load();
  }, [load, approved]);

  if (error) return <div className="corpus-error" role="alert">{error}</div>;
  if (!data) return <div className="state-msg">Loading invitations…</div>;
  if (!approved && data.items.length === 0) {
    return <div className="state-msg">Approve this company first. Only approved companies are invited.</div>;
  }
  if (approved && people.length === 0 && data.items.length === 0) {
    return <div className="state-msg">Find people on the People tab first. Each invitation is for one person.</div>;
  }

  // The newest invitation per person decides what is shown (an older one may be revoked).
  // Invitations are listed newest first, so the first one seen per person wins.
  const latest = {};
  data.items.forEach((inv) => {
    const key = inv.contact_id || `removed-${inv.id}`;
    if (!latest[key]) latest[key] = inv;
  });
  const order = { first: 0, backup: 1 };
  const listed = approved ? people : [];
  const rows = [...listed]
    .sort((a, b) => (order[a.priority] ?? 2) - (order[b.priority] ?? 2))
    .map((person) => ({ person, invitation: latest[person.id], canCreate: true }));
  // Keep the history of people who are no longer listed (removed, or the company was dismissed).
  const shown = new Set(listed.map((p) => p.id));
  Object.entries(latest).forEach(([key, inv]) => {
    if (shown.has(key)) return;
    rows.push({
      person: { id: key, full_name: inv.contact_name || 'Person removed', title: inv.contact_title },
      invitation: inv,
      canCreate: false,
    });
  });

  async function changed() {
    await load();
    onChanged();
  }

  const blockedIds = new Set(data.do_not_contact || []);
  const needingLink = rows.filter(
    ({ person, invitation, canCreate }) => canCreate && !blockedIds.has(person.id) && (!invitation || invitation.revoked_at),
  ).length;

  async function createAll() {
    setBulkBusy(true);
    setBulkNote('');
    setError('');
    try {
      const result = await createAllCorpusInvitations(company.id);
      const skipped = result.skipped ? ` ${result.skipped} already had one.` : '';
      setBulkNote(`Created ${result.created} link${result.created === 1 ? '' : 's'}.${skipped}`);
      await changed();
    } catch (e) {
      setError(e.message);
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <>
      <p className="corpus-hint">Each person gets their own link. Registrations show up here within minutes.</p>
      {!approved && (
        <div className="corpus-notice" role="status">
          This company is no longer approved, so no new links can be made. Links already sent still work in MenaOS: revoke any that should stop.
        </div>
      )}
      {approved && !data.links_configured && (
        <div className="corpus-error" role="alert">Invitation links are not set up yet: an admin needs to set MENAOS_PUBLIC_URL in backend/.env to the https:// address of MenaOS.</div>
      )}
      {approved && <CompanyStop company={company} stopped={data.invitations_stopped} onChanged={changed} />}
      {approved && !data.invitations_stopped && data.links_configured && needingLink > 0 && (
        <div className="corpus-actions">
          <button type="button" className="approve" disabled={bulkBusy} onClick={createAll}>
            {bulkBusy ? 'Creating…' : `Create links for everyone ready (${needingLink})`}
          </button>
        </div>
      )}
      {bulkNote && <div className="corpus-notice" role="status">{bulkNote}</div>}
      <ul className="corpus-invites">
        {rows.map(({ person, invitation, canCreate }) => (
          <PersonInvitation
            key={person.id}
            company={company}
            person={person}
            invitation={invitation}
            canCreate={approved && canCreate && !data.invitations_stopped}
            linksConfigured={data.links_configured}
            canEmail={Boolean(data.email_configured)}
            blocked={(data.do_not_contact || []).includes(person.id)}
            onChanged={changed}
          />
        ))}
      </ul>
    </>
  );
}

const SYNC_TEXT = {
  up_to_date: (h) => ({ label: 'Up to date', text: `MenaOS checked ${ago(h.last_checked_at)}.` }),
  waiting: () => ({ label: 'Waiting on MenaOS', text: 'New registrations are being saved in MenaOS and will appear shortly.' }),
  failing: (h) => ({ label: "Can't reach MenaOS", text: `Registration updates stopped ${h.failing_since ? `at ${formatWhen(h.failing_since)}` : ''}. MenaGTM keeps trying.` }),
  not_connected: () => ({ label: 'Not connected', text: 'Registration updates from MenaOS are off.' }),
  pending: () => ({ label: 'Checking MenaOS', text: 'Registration updates have not been checked yet.' }),
};

const SYNC_LEVEL = { up_to_date: 'ok', waiting: 'warn', failing: 'bad', not_connected: 'idle', pending: 'idle' };
const RUN_OUTCOME = {
  ok: 'Read',
  held_back: 'Read, some waiting in MenaOS',
  failed: 'Failed',
  not_connected: 'Not connected',
};
const RUN_REASON = {
  not_configured: 'not set up',
  feed_off: 'feed switched off in MenaOS',
  key: 'key refused',
  cursor: 'position refused',
  unavailable: 'MenaOS did not answer',
  bad_response: 'unexpected answer',
};
const REFRESH_MS = 60000;

// One registration whose link MenaGTM did not create. An admin decides; nothing is matched by guessing.
function UnmatchedItem({ item, invitations, onResolved }) {
  const [mode, setMode] = useState('');
  const [invitationId, setInvitationId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = mode === 'attach'
        ? await attachMenaosRegistration(item.reference, invitationId, note.trim())
        : await dismissMenaosRegistration(item.reference, note.trim());
      onResolved(result.items);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  return (
    <li className="corpus-unmatched">
      <span className="corpus-mono">{item.reference}</span> · {formatWhen(item.submitted_at)}
      {!mode && (
        <div className="corpus-unmatched-actions">
          <button type="button" onClick={() => setMode('attach')} disabled={!invitations.length}>Attach to an invitation</button>
          <button type="button" onClick={() => setMode('dismiss')}>Dismiss</button>
        </div>
      )}
      {mode && (
        <form className="corpus-unmatched-form" onSubmit={submit}>
          {mode === 'attach' && (
            <select aria-label={`Invitation for ${item.reference}`} value={invitationId} onChange={(e) => setInvitationId(e.target.value)}>
              <option value="">Choose the invitation it belongs to…</option>
              {invitations.map((inv) => (
                <option key={inv.id} value={inv.id}>
                  {inv.company} · {inv.person || 'person removed'} · {INVITATION_LABELS[inv.state] || inv.state}
                </option>
              ))}
            </select>
          )}
          <input
            aria-label={`Note for ${item.reference}`}
            placeholder={mode === 'attach' ? 'Why, e.g. the link was mistyped' : 'Why, e.g. an internal test'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {error && <div className="corpus-error" role="alert">{error}</div>}
          <div className="corpus-unmatched-actions">
            <button type="submit" disabled={busy || note.trim().length < 3 || (mode === 'attach' && !invitationId)}>
              {mode === 'attach' ? 'Confirm attach' : 'Confirm dismiss'}
            </button>
            <button type="button" onClick={() => { setMode(''); setError(''); }}>Cancel</button>
          </div>
        </form>
      )}
    </li>
  );
}

function AdminSyncPanel({ health, onRefresh }) {
  const [running, setRunning] = useState(false);
  const [unmatched, setUnmatched] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    listMenaosUnmatched().then((r) => setUnmatched(r.items)).catch(() => setUnmatched(null));
  }, [health.last_checked_at]);

  useEffect(() => {
    if (unmatched && unmatched.length) {
      listAllCorpusInvitations().then((r) => setInvitations(r.items)).catch(() => setInvitations([]));
    }
  }, [unmatched]);

  async function resolved(items) {
    setUnmatched(items);
    await onRefresh();
  }

  async function restart() {
    setRunning(true);
    setError('');
    try {
      await restartMenaosSync();
      await runMenaosSync();
      await onRefresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  }

  async function checkNow() {
    setRunning(true);
    setError('');
    try {
      await runMenaosSync();
      await onRefresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="corpus-sync-panel" role="region" aria-label="MenaOS connection">
      {health.reason_text && <p className="corpus-line">{health.reason_text}</p>}
      {error && <div className="corpus-error" role="alert">{error}</div>}
      <button type="button" disabled={running} onClick={checkNow}>{running ? 'Checking…' : 'Check now'}</button>
      {health.reason === 'cursor' && (
        <div className="corpus-sync-restart">
          <p className="corpus-line">
            Reading again from the start is safe: registrations already saved are skipped and reviews are kept.
          </p>
          <button type="button" disabled={running} onClick={restart}>Read again from the start</button>
        </div>
      )}
      <h4>LAST READS</h4>
      {health.runs && health.runs.length ? (
        <ul className="corpus-sync-runs">
          {health.runs.map((r) => (
            <li key={`${r.started_at}-${r.trigger}`}>
              {formatWhen(r.started_at)} · {RUN_OUTCOME[r.outcome] || r.outcome}{r.reason ? ` (${RUN_REASON[r.reason] || r.reason})` : ''}
              {' '}· {r.new} new, {r.matched} matched{r.trigger === 'manual' ? ' · Check now' : ''}
            </li>
          ))}
        </ul>
      ) : <p className="corpus-line">No reads yet.</p>}
      <h4>UNMATCHED REGISTRATIONS</h4>
      {unmatched === null ? <p className="corpus-line">…</p> : unmatched.length === 0 ? (
        <p className="corpus-line">None. Every registration matched an invitation.</p>
      ) : (
        <>
          <p className="corpus-line">
            {unmatched.length} came with a link MenaGTM did not create. Attach each one to the invitation it belongs to, or dismiss it. Nothing is attached by guessing.
          </p>
          <ul className="corpus-sync-runs corpus-unmatched-list">
            {unmatched.map((u) => (
              <UnmatchedItem key={u.reference} item={u} invitations={invitations} onResolved={resolved} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export function SyncHealth({ isAdmin }) {
  const [health, setHealth] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setHealth(await getMenaosSync());
    } catch {
      setHealth(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  if (!health) return null;
  const view = (SYNC_TEXT[health.state] || SYNC_TEXT.pending)(health);
  return (
    <div className="corpus-sync">
      <span className={`corpus-sync-pill level-${SYNC_LEVEL[health.state] || 'idle'}`} role="status" title={view.text}>
        {view.label}
      </span>
      <span className="corpus-credit-note">{view.text}</span>
      {isAdmin && (
        <button type="button" className="corpus-sync-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide details' : 'Details'}
        </button>
      )}
      {isAdmin && open && <AdminSyncPanel health={health} onRefresh={refresh} />}
    </div>
  );
}
